import { config } from "./config.js";

/**
 * The bubble in the corner.
 *
 * Deliberately written without a framework. This is the only code that runs for a
 * shopper who never opens the chat — which is nearly all of them — so it stays a few
 * kilobytes of DOM calls rather than pulling in a renderer to draw one button.
 *
 * It also owns the shadow root. Everything the widget renders lives inside it, which
 * is what stops the shop's stylesheet from reshaping the chat and stops the chat's
 * styles from leaking into the shop. A WooCommerce theme with a global
 * `button { width: 100% }` would otherwise be enough to break the layout.
 */

export interface Launcher {
  readonly shadow: ShadowRoot;
  /** Where the panel mounts. Inside the shadow root, beside the button. */
  readonly panelHost: HTMLElement;
  setOpen(open: boolean): void;
  /**
   * Collapse the panel from inside it, as the minimise button does.
   *
   * Distinct from `setOpen(false)`, which only reflects a decision already made. This
   * one *makes* the decision, so it notifies the toggle handlers and the panel and the
   * bubble end up agreeing. Without it the panel would hide while the launcher still
   * believed it was open, and the next tap on the bubble would appear to do nothing.
   */
  close(): void;
  /** Number shown on the bubble while the panel is closed. */
  setUnread(count: number): void;
  onToggle(handler: (open: boolean) => void): void;
  /**
   * Say something beside the bubble.
   *
   * Two callers, one mechanism. On load it shows the shop's teaser; later the panel
   * uses it to surface a reply that arrived while the chat was collapsed — a customer
   * who went back to browsing gets the answer brought to them rather than having to
   * notice a badge.
   */
  showTeaser(text: string): void;
  hideTeaser(): void;
}

/**
 * Once per browsing session, not once per page.
 *
 * A shopper moves between product pages constantly. A teaser that reappeared on every
 * navigation would be the widget nagging, and `sessionStorage` is what makes
 * "dismissed" mean dismissed for the visit.
 */
const TEASER_DISMISSED_KEY = "island_teaser_dismissed";

/** Long enough that the teaser follows the page rather than competing with it. */
const TEASER_DELAY_MS = 3_000;

function teaserDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(TEASER_DISMISSED_KEY) === "1";
  } catch {
    // Storage can be unavailable — Safari private browsing, a strict policy. Showing
    // the teaser is the lesser error; suppressing it entirely would hide the feature.
    return false;
  }
}

function rememberTeaserDismissed(): void {
  try {
    window.sessionStorage.setItem(TEASER_DISMISSED_KEY, "1");
  } catch {
    // Nothing to do. It reappears on the next page, which is a nuisance rather than
    // a fault.
  }
}

/**
 * Layout for the host element, applied inline rather than through `:host`.
 *
 * `:host` rules lose the cascade to the page's own stylesheet. The shadow root
 * protects what is *inside* it, but the host itself is an ordinary `<div>` in the
 * shop's document, and a theme rule as unremarkable as `body div { position: static }`
 * outranks anything `:host` declares. The result is a bubble that mounted correctly
 * and is nowhere on screen — which looks exactly like a widget that never loaded.
 *
 * Inline declarations marked `!important` are the one thing an author stylesheet
 * cannot outrank, so the handful of properties that decide whether the widget is
 * visible at all are pinned here. Everything cosmetic stays in the stylesheet below,
 * where it belongs.
 */
const HOST_LAYOUT: Readonly<Record<string, string>> = {
  position: "fixed",
  right: "20px",
  bottom: "20px",
  top: "auto",
  left: "auto",
  width: "auto",
  height: "auto",
  margin: "0",
  padding: "0",
  border: "none",
  transform: "none",
  "max-width": "none",
  "max-height": "none",
  "min-width": "0",
  "min-height": "0",
  float: "none",
  clip: "auto",
  "clip-path": "none",
  opacity: "1",
  visibility: "visible",
  display: "block",
  "pointer-events": "auto",
  /* A stacking context of its own, so the shop's own z-index cannot bury it. */
  "z-index": "2147483000",
};

const BUTTON_STYLES = `
:host {
  all: initial;
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483000;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
* { box-sizing: border-box; }
.launcher {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  /* Deep to bright, so the bubble reads as an object rather than a flat disc. The
     accent end is the shop's configured colour; the anchor end is the brand navy. */
  background: linear-gradient(135deg, var(--island-deep) 0%, var(--island-primary) 130%);
  color: #fff;
  box-shadow: 0 12px 30px rgb(13 59 93 / 0.35);
  display: grid;
  place-items: center;
  transition: transform 0.15s ease;
}
.launcher:hover { transform: scale(1.06); }
.launcher:focus-visible { outline: 3px solid #fff; outline-offset: 2px; }
.launcher svg { width: 28px; height: 28px; fill: currentColor; }
/* Presence, not availability: the widget answers at any hour. Whether a *person* is
   there is a different claim, and the panel makes it with the schedule behind it. */
.online {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #22c55e;
  border: 2.5px solid #fff;
  pointer-events: none;
}
.teaser {
  position: absolute;
  right: 74px;
  bottom: 12px;
  display: none;
  align-items: center;
  gap: 10px;
  max-width: min(290px, calc(100vw - 110px));
  padding: 13px 18px;
  border-radius: 999px;
  border: none;
  background: #fff;
  color: #12283a;
  font: inherit;
  font-size: 14px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 10px 30px rgb(13 59 93 / 0.18);
  animation: island-rise 0.4s ease;
}
.teaser[data-visible="true"] { display: flex; }
.teaser .text {
  /* One line, clipped. A teaser that wraps to four lines is a dialog nobody asked
     for, and the full text is one tap away. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.teaser .dismiss {
  flex: none;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: #eef3f7;
  color: #7a94a8;
  font: inherit;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.teaser .dismiss:hover { background: #dfe7ee; }
@keyframes island-rise {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
.badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #dc2626;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: none;
  align-items: center;
  justify-content: center;
}
.badge[data-visible="true"] { display: flex; }
.wrap { position: relative; }
.panel-host { display: none; }
.panel-host[data-open="true"] { display: block; }
@media (prefers-reduced-motion: reduce) {
  .launcher { transition: none; }
  .teaser { animation: none; }
}
`;

/**
 * The dark end of the launcher's gradient, and the panel's chrome colour.
 *
 * Fixed rather than configurable, unlike the accent. Two colours to configure is two
 * chances to produce a gradient that reads as a mistake, and the shop's identity is
 * carried by the accent that sits on top of it.
 */
const DEEP_ACCENT = "#0d3b5d";

const CHAT_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3C6.98 3 3 6.58 3 11c0 2.35 1.14 4.44 2.95 5.85L5 21l4.45-2.06c.8.2 1.66.31 2.55.31 5.02 0 9-3.58 9-8s-3.98-8-9-8Z"/></svg>`;
const CLOSE_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41Z"/></svg>`;

export function mountLauncher(): Launcher {
  const host = document.createElement("div");
  host.id = "island-ai-widget";

  for (const [property, value] of Object.entries(HOST_LAYOUT)) {
    host.style.setProperty(property, value, "important");
  }

  document.body.append(host);

  // Open rather than closed: a closed root is marginally more isolated and makes the
  // widget impossible to inspect from devtools, which costs more in support than the
  // isolation is worth on a page we own.
  const shadow = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = BUTTON_STYLES;
  shadow.append(style);

  const wrap = document.createElement("div");
  wrap.className = "wrap";
  wrap.style.setProperty("--island-primary", config.primaryColor);
  wrap.style.setProperty("--island-deep", DEEP_ACCENT);

  const panelHost = document.createElement("div");
  panelHost.className = "panel-host";

  const button = document.createElement("button");
  button.className = "launcher";
  button.type = "button";
  button.setAttribute("aria-label", config.launcherLabel);
  button.setAttribute("aria-expanded", "false");
  button.innerHTML = CHAT_ICON;

  const online = document.createElement("span");
  online.className = "online";

  const badge = document.createElement("span");
  badge.className = "badge";
  badge.setAttribute("aria-live", "polite");

  /**
   * A button, not a div.
   *
   * It is clickable and it opens the chat, so it has to be reachable by keyboard and
   * announced as an action. The dismiss control inside it is a second button, and its
   * click is stopped from reaching this one.
   */
  const teaser = document.createElement("button");
  teaser.className = "teaser";
  teaser.type = "button";

  const teaserText = document.createElement("span");
  teaserText.className = "text";

  const teaserDismiss = document.createElement("span");
  teaserDismiss.className = "dismiss";
  teaserDismiss.setAttribute("role", "button");
  teaserDismiss.setAttribute("aria-label", "Dismiss");
  teaserDismiss.textContent = "✕";

  teaser.append(teaserText, teaserDismiss);

  wrap.append(panelHost, teaser, button, online, badge);
  shadow.append(wrap);

  let open = false;
  const handlers: ((open: boolean) => void)[] = [];

  function showTeaser(text: string): void {
    if (open || text === "") return;

    teaserText.textContent = text;
    teaser.dataset.visible = "true";
  }

  function hideTeaser(): void {
    teaser.dataset.visible = "false";
  }

  function setOpen(next: boolean): void {
    open = next;
    panelHost.dataset.open = String(next);
    button.innerHTML = next ? CLOSE_ICON : CHAT_ICON;
    button.setAttribute("aria-expanded", String(next));
    button.setAttribute("aria-label", next ? "Close chat" : config.launcherLabel);
    // The panel covers the bubble on a phone, and a teaser floating over an open
    // chat is the widget talking over itself.
    online.style.display = next ? "none" : "";

    if (next) {
      hideTeaser();
      setUnread(0);
    }
  }

  function setUnread(count: number): void {
    // Hidden while the panel is open: an unread count next to the messages the
    // customer is currently reading is noise.
    const visible = count > 0 && !open;
    badge.dataset.visible = String(visible);
    badge.textContent = count > 9 ? "9+" : String(count);
  }

  function toggle(next: boolean): void {
    setOpen(next);
    for (const handler of handlers) handler(next);
  }

  button.addEventListener("click", () => {
    toggle(!open);
  });

  teaser.addEventListener("click", (event) => {
    // The dismiss control lives inside the teaser button, so its click would
    // otherwise open the chat — the opposite of what it says.
    if (event.target === teaserDismiss) {
      event.stopPropagation();
      hideTeaser();
      rememberTeaserDismissed();
      return;
    }

    hideTeaser();
    toggle(true);
  });

  /**
   * The shop's teaser, once the page has settled.
   *
   * Skipped entirely when the shop has not written one, and when the visitor has
   * already waved it away this session. `open` is re-checked on the timeout because
   * three seconds is long enough for somebody to have opened the chat themselves.
   */
  if (config.teaser !== "" && !teaserDismissed()) {
    const timer = setTimeout(() => {
      showTeaser(config.teaser);
    }, TEASER_DELAY_MS);

    // Nothing here should hold a page open, and a widget that delays unload is a
    // widget somebody eventually removes.
    if (typeof timer === "object" && "unref" in timer) timer.unref();
  }

  return {
    shadow,
    panelHost,
    setOpen,
    close(): void {
      toggle(false);
    },
    setUnread,
    onToggle(handler): void {
      handlers.push(handler);
    },
    showTeaser,
    hideTeaser,
  };
}
